import { EyeIcon, SendIcon } from "lucide-react";
import { Button } from "./button";
import { Applicant } from "@type/applicant";
import { getMatchBadgeClasses } from "@util/job";
import { getApplicationStatusClass } from "@util/string";
import { useState } from "react";
import { applicationStatusMap } from "@constant/app";
import { Tooltip } from "./tooltip";
import { InviteToApplyRequest, Job } from "@type/job";
import InviteConfirmationDialogV2 from "./invite-confirmation-v2-dialog";
import { useSnackbar } from "@hook/use-snackbar";
import { inviteToApply } from "@service/job";

interface Props {
    applicant: Applicant
    job: Job | null
    isResend: boolean
    isDisabledInvitation: boolean
    onViewProfile: () => void
    onLoad: () => void
}

export default function CandidateItem(props: Props) {
    const snackbar = useSnackbar();
    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const [isLoadingInvitation, setLoadingInvitation] = useState<boolean>(false);

    const handleInviteConfirm = async (data: InviteToApplyRequest) => {
        setLoadingInvitation(true)
        try {
            const resp = await inviteToApply(data)
            if (resp.code === 201) {
                snackbar.showSuccess(resp.message || "Invitation sent successfully.");
                props.onLoad();
            } else {
                snackbar.showError(resp.message || "Failed to send invitation.");
            }

            setLoadingInvitation(false)
        } catch (error) {
            if (error instanceof Error) {
                console.error("failed to invite to apply", error.message);
            } else {
                console.error("failed to invite to apply", error);
            }
            setLoadingInvitation(false)
        }
    };

    return (
        <>
            <tr key={props.applicant.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="px-4 py-4 whitespace-nowrap text-center">
                    <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full overflow-hidden">
                            <img
                                className="h-full w-full object-cover"
                                src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${props.applicant.profile_picture_path || ""}`}
                                onError={(e) => {
                                    e.currentTarget.src = `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/default_avatar.png`;
                                    e.currentTarget.onerror = null; // Prevent infinite loop
                                }}
                            />
                        </div>
                        <div className="ml-4">
                            <div className="flex flex-col justify-start items-start align-top">
                                <div className="font-medium">
                                    {props.applicant.applicant_name || props.applicant.cv_data?.CV_data?.name || "—"}
                                </div>
                                <div className="text-sm text-gray-600">
                                    <div className="max-w-[350px] break-words">
                                        {props.applicant.cv_data?.CV_data?.position || ""}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </td>
                <td className="px-4 py-4 text-center">
                    <div className="max-w-[300px] break-words text-center text-sm text-gray-500">
                        {props.applicant.cv_data?.CV_data?.location || "—"}
                    </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-center">
                    <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getApplicationStatusClass(
                            props.applicant.status === applicationStatusMap.invitedFUSent ? applicationStatusMap.invitationExpired : props.applicant.status,
                        )}`}
                    >
                        {props.applicant.status === applicationStatusMap.invitedFUSent ? applicationStatusMap.invitationExpired : props.applicant.status}
                    </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-center">
                    <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getMatchBadgeClasses(
                            Number(props.applicant.similarity_score) || 0,
                        )}`}
                    >
                        {Number(props.applicant.similarity_score) || 0}% match
                    </span>
                </td>
                <td className="w-[300px] px-4 py-4 whitespace-nowrap text-center text-sm font-medium">
                    <div className="flex justify-center space-x-2">
                        <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-1"
                            onClick={props.onViewProfile}
                        >
                            <EyeIcon
                                className="h-4 w-4"
                            />
                            View Profile
                        </Button>
                        <Tooltip
                            content={(
                                <>
                                    An auto-reminder email is sent to the candidate after 24 hours.<br />
                                    You may resend an invite again in 72 hours.
                                </>
                            )}
                            side="top"
                            delayDuration={0}
                        >
                            <div className="inline-block">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex items-center gap-1 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white transition-colors"
                                    onClick={() => setInviteDialogOpen(true)}
                                    disabled={props.isDisabledInvitation || isLoadingInvitation}
                                >
                                    {isLoadingInvitation ? (
                                        <>
                                            <svg className="animate-spin h-4 w-4 text-blue-600 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Please wait
                                        </>
                                    ) : (
                                        <>
                                            <SendIcon className="h-4 w-4 mr-1" />
                                            {props.isResend ? "Resend Invitation" : "Invite to Apply"}
                                        </>
                                    )}
                                </Button>
                            </div>
                        </Tooltip>
                    </div>
                </td>
            </tr>
            <InviteConfirmationDialogV2
                isOpen={inviteDialogOpen}
                onClose={setInviteDialogOpen}
                onConfirm={handleInviteConfirm}
                candidateName={props.applicant.applicant_name || ""}
                candidate_id={props.applicant.applicant_id || ""}
                currentJob={props.job}
            />
        </>
    )
}